import { useEffect, useCallback, useState } from "react"
import { createContainer } from "unstated-next"
import { Contract as MulticallContract } from "ethers-multicall"
import { big2Decimal, bigNum2Big } from "util/format"
import { Big } from "big.js"
import { Contract } from "container/contract"
import { Transaction } from "container/transaction"
import { BIG_ZERO } from "../../constant/number"
import { User } from "../user"
import { getDuration } from "util/time"
import { useContractCall } from "../../hook/useContractCall"
import { Connection } from "../connection"
import { useContractEvent } from "../../hook/useToken"

export const Staking = createContainer(useStaking)

interface WithdrawTime {
    humanReadable: number[]
    secondsUnit: number
    expirationTimestamp: number
}

const defaultWithdrawTime = {
    humanReadable: [0, 0, 0, 0],
    secondsUnit: 0,
    expirationTimestamp: 0,
}

export enum CoolDownStatus {
    init,
    onGoing,
    readyToWithdraw,
}

const STAKED_PERP_DECIMAL = 18

function useStaking() {
    const { signer, multicallProvider } = Connection.useContainer()
    const { StakePerpToken } = Contract.useContainer()
    const { execute, executeWithGasLimit } = Transaction.useContainer()
    const {
        state: { address },
    } = User.useContainer()
    const [coolDownPeriod, setCoolDownPeriod] = useState(0)
    const [withdrawPendingBalance, setWithdrawPendingBalance] = useState(BIG_ZERO)
    const [withdrawTime, setWithdrawTime] = useState<WithdrawTime>(defaultWithdrawTime)
    const [cooldownStatus, setCoolDownStatus] = useState(CoolDownStatus.init)

    const getWithdrawableTimestamp = useContractCall(
        async (address: string) => {
            const timestamp = await StakePerpToken!.stakerCooldown(address)
            const now = Math.floor(new Date().getTime() / 1000)
            const timeDiff = timestamp.toNumber() - now
            return {
                humanReadable: getDuration(timeDiff),
                secondsUnit: timeDiff <= 0 ? 0 : timeDiff,
                expirationTimestamp: timestamp.toNumber(),
            }
        },
        [StakePerpToken],
    )

    const updateWithdrawableTimestamp = useCallback(
        async (address: string) => {
            setWithdrawTime(await getWithdrawableTimestamp(address))
        },
        [getWithdrawableTimestamp],
    )

    useEffect(() => {
        async function getStakingInfo() {
            if (StakePerpToken && multicallProvider) {
                const multiContractStakePerpToken = new MulticallContract(
                    StakePerpToken.address,
                    StakePerpToken.interface.fragments,
                )

                const [cooldownPeriod, withdrawPendingBalance] = await multicallProvider.all([
                    multiContractStakePerpToken.cooldownPeriod(),
                    multiContractStakePerpToken.stakerWithdrawPendingBalance(address),
                ])
                await updateWithdrawableTimestamp(address)

                setCoolDownPeriod(bigNum2Big(cooldownPeriod, 0).toNumber())
                setWithdrawPendingBalance(bigNum2Big(withdrawPendingBalance, STAKED_PERP_DECIMAL))
            }
        }
        if (address) {
            getStakingInfo()
        }
    }, [multicallProvider, StakePerpToken, address, updateWithdrawableTimestamp])

    useEffect(() => {
        const { secondsUnit } = withdrawTime
        setCoolDownStatus(
            withdrawPendingBalance.gt(BIG_ZERO)
                ? secondsUnit === 0
                    ? CoolDownStatus.readyToWithdraw
                    : CoolDownStatus.onGoing
                : CoolDownStatus.init,
        )
    }, [withdrawPendingBalance, withdrawTime])

    const updateWithdrawPendingBalance = useContractCall(
        async (address: string) => {
            const balance = await StakePerpToken!.stakerWithdrawPendingBalance(address)
            setWithdrawPendingBalance(bigNum2Big(balance, STAKED_PERP_DECIMAL))
        },
        [StakePerpToken],
    )

    const stake = useContractCall(
        async (amount: Big, onSuccess?: Function) => {
            const receipt = await executeWithGasLimit(StakePerpToken!.connect(signer), "stake", [big2Decimal(amount)])
            if (onSuccess) {
                onSuccess()
            }
            return receipt
        },
        [
            // execute,
            executeWithGasLimit,
            StakePerpToken,
            signer,
        ],
    )

    const unstake = useCallback(async () => {
        await executeWithGasLimit(StakePerpToken!.connect(signer), "unstake", [])
    }, [
        StakePerpToken,
        // execute,
        executeWithGasLimit,
        signer,
    ])

    const withdraw = useContractCall(
        async (onSuccess?: Function) => {
            await execute(StakePerpToken!.connect(signer).withdraw())
            if (onSuccess) {
                onSuccess()
            }
        },
        [StakePerpToken, execute, signer],
    )

    useContractEvent(StakePerpToken, "Withdrawn", async (msgSender: string) => {
        if (msgSender === address) {
            await Promise.all([updateWithdrawPendingBalance(address), updateWithdrawableTimestamp(address)])
        }
    })

    useContractEvent(StakePerpToken, "Unstaked", async (msgSender: string) => {
        if (msgSender === address) {
            await Promise.all([updateWithdrawPendingBalance(address), updateWithdrawableTimestamp(address)])
        }
    })

    useContractEvent(StakePerpToken, "Staked", async (msgSender: string) => {
        if (msgSender === address) {
            await Promise.all([updateWithdrawPendingBalance(address), updateWithdrawableTimestamp(address)])
        }
    })

    return {
        // actions
        stake,
        unstake,
        withdraw,
        getWithdrawableTimestamp,
        // no required login data
        coolDownPeriod,
        setCoolDownStatus,
        // required login data
        cooldownStatus,
        withdrawPendingBalance,
        withdrawTime,
    }
}
